import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getAllBlogPosts, getActiveSlides } from '../../utils/firestore';
import BlogPostCard from '../../components/blog/BlogPostCard';
import Newsletter from '../../components/common/Newsletter';
import SEO from '../../components/seo/SEO';
import BusinessSchema from '../../components/seo/BusinessSchema';

// Import images (these will be placeholder paths for now)
// import heroImage1 from '../../assets/hero1.jpg';
// import heroImage2 from '../../assets/hero2.jpg';
// import dietitianImage from '../../assets/dietitian.jpg';

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [recentPosts, setRecentPosts] = useState([]);
  const [heroSlides, setHeroSlides] = useState([
    { 
      id: 1, 
      image: "/placeholder-hero1.jpg", 
      title: "Kişiye Özgü Diyet Plan Programı", 
      subtitle: "Kişiye Özgü Diyet Plan Programı" 
    },
    { 
      id: 2, 
      image: "/placeholder-hero2.jpg", 
      title: "Kişiye Özgü Diyet Plan Programı", 
      subtitle: "Kişiye Özgü Diyet Plan Programı" 
    }
  ]);

  // Hero slider images
  // const heroSlides = [
  //   { 
  //     id: 1, 
  //     image: "/placeholder-hero1.jpg", 
  //     title: "Healthy Living Starts Here", 
  //     subtitle: "Personalized nutrition plans for your wellness journey" 
  //   },
  //   { 
  //     id: 2, 
  //     image: "/placeholder-hero2.jpg", 
  //     title: "Nutrition That Fits Your Life", 
  //     subtitle: "Achieve your health goals with expert guidance" 
  //   }
  // ];

  // Why choose us data
  const whyChooseUs = [
  {
    id: 1,
    title: "Kişiye Özel Yaklaşım",
    description: "İhtiyaçlarınıza ve yaşam tarzınıza göre özelleştirilmiş beslenme planları."
  },
  {
    id: 2,
    title: "Bilimsel Temelli",
    description: "En güncel araştırmalara dayalı, bilim destekli beslenme önerileri."
  },
  {
    id: 3,
    title: "Destek ve Motivasyon",
    description: "Senin beslenme yolculuğunda sürekli iletişim ve motivasyon ile adım adım hayalindeki bedene ulaşma."
  },
  {
    id: 4,
    title: "Sürdürülebilir Sonuçlar",
    description: "Kısa vadeli çözümler yerine uzun vadeli sağlıklı alışkanlıklara odaklanma."
  }
];

  // Services data
  const services = [
  {
    id: 1,
    title: "Kilo Yönetimi",
    description: "Sağlıklı kilo verme veya alma için kişiye özel planlar."
  },
  {
    id: 2,
    title: "Beslenme Danışmanlığı ve Yaşam Tarzı Değişikliği",
    description: "Kişiye özel alışkanlık değişiklikleri, sağlıklı yaşam ve uzun vadeli beslenme hedefleri oluşturma."
  },
  {
    id: 3,
    title: "Hastalıklarda Beslenme",
    description: "Hipertansiyon, böbrek hastalıkları, karaciğer hastalıkları, tiroid sorunları gibi durumlarda özel diyet planları."
  },
  {
    id: 4,
    title: "Sindirim Sağlığı",
    description: "Bağırsak sağlığını destekleyen beslenme stratejileri."
  }
];

  // Auto-rotate hero slider
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [heroSlides.length]);

  // Fetch active slides from Firestore
  useEffect(() => {
    const fetchSlides = async () => {
      try {
        const slides = await getActiveSlides();
        if (slides.length > 0) {
          setHeroSlides(slides);
        }
      } catch (error) {
        console.error('Error fetching slides:', error);
      }
    };

    fetchSlides();
  }, []);

  // Fetch recent blog posts
  useEffect(() => {
    const fetchRecentPosts = async () => {
      try {
        const posts = await getAllBlogPosts();
        setRecentPosts(posts.slice(0, 4)); // Get only the 4 most recent posts
      } catch (error) {
        console.error('Error fetching recent posts:', error);
      }
    };

    fetchRecentPosts();
  }, []);

  return (
    <div className="w-full">
      <SEO 
        title="Diyetisyen Nihal Kamış - Kişiye Özgü Diyet Plan Programı | İstanbul"
        description="İstanbul merkezli diyetisyen Nihal Kamış'tan kişiye özgü diyet planları, beslenme danışmanlığı ve sağlıklı yaşam rehberleri. Kilo verme, kilo alma, sporcu beslenmesi ve hastalıklarda beslenme konularında danışmanlık."
        url="https://www.dytnihalkamis.com"
        keywords="diyetisyen, beslenme danışmanı, diyetisyen nihal kamış, diyetisyen istanbul, diyet programı, kilo verme, kilo alma, sağlıklı beslenme, beslenme planı, sporcu beslenmesi, hastalıklarda beslenme"
      />
      <BusinessSchema />
      
      {/* Hero Section */}
      <section 
        className="relative home-hero flex items-center"
        style={{ backgroundImage: `url(${heroSlides[currentSlide].image})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
        <div className="container relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl md:text-4xl lg:text-6xl font-bold text-white mb-12 mt-6">
                {heroSlides[currentSlide].title}
              </h1>
              <p className="text-xl text-white mb-8">
                {heroSlides[currentSlide].subtitle}
              </p>
              <Link 
                to="/contact" 
                className="btn btn-accent text-lg px-8 py-4 mb-4"
              >
                İletişime Geçin
              </Link>
            </motion.div>
            
            <div className="relative">
              {/* Hero slider indicators */}
              <div className="absolute bottom-4 right-0 z-20 flex space-x-2">
                {heroSlides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-3 h-3 rounded-full ${
                      currentSlide === index ? 'bg-white' : 'bg-gray-400'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section-padding bg-white">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 {*/items-center*/} mb-16">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="relative">
                <img 
                  src="/assets/placeholder-dietitian.png" 
                  alt="Dietitian Nihal Kamış" 
                  className="rounded-lg shadow-xl w-full"
                />
				
                {/*<div className="absolute -bottom-6 -right-6 w-24 h-24 bg-nihal-primary rounded-full flex items-center justify-center">
                  <span className="text-white text-2xl font-bold">NK</span>
                </div>*/}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold text-nihal-dark mb-4">Dyt. Nihal Kamış Hakkında</h2>
              <p className="text-gray-600 mb-4">
				Diyetisyen Nihal Kamış, İstanbul Medipol Üniversitesi Beslenme ve Diyetetik Bölümü’nden dereceyle mezun olmuştur. Eğitim süresince Medipol Üniversitesi Hastaneleri ve Diyetisyen Merve Sayın Beslenme ve Diyet Eğitim Merkezi’nde staj yaparak uygulamalı deneyim kazanmıştır.
				</p>
				<p className="text-gray-600 mb-4">
				Gebe ve anne-çocuk beslenmesi, sporcu beslenmesi, bariyatrik cerrahi diyetisyenliği, obezite yönetimi ve hastalıklarda beslenme gibi alanlarda kapsamlı bilgi ve deneyime sahiptir. Spor beslenmesi kongresi ve çeşitli eğitim programlarına katılarak bilgilerini güncel tutmaktadır.
				</p>
				<p className="text-gray-600 mb-6">
				Son üç yıldır aktif olarak diyetisyenlik yapmaktadır. Bireylerin sağlıklı ve dengeli beslenme alışkanlıkları kazanmalarına destek olmaktadır.
				</p>
				<Link to="/about" className="btn btn-primary">
				Daha Fazla Bilgi
				</Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Recent Blog Posts */}
      <section className="section-padding bg-nihal-light">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-nihal-dark mb-4">Blog Yazılarım</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Beslenme, sağlıklı yaşam ve iyi yaşam üzerine en yeni bilgileri keşfedin.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {recentPosts.map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <BlogPostCard post={post} />
              </motion.div>
            ))}
          </div>
          
          <div className="text-center mt-10">
            <Link to="/blog" className="btn btn-secondary">
              Tüm Yazılarımı Görüntüle
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-white">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-nihal-dark mb-4">Neden Beni Seçmelisiniz?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              İhtiyaçlarınız ve hedeflerinize özel, kanıta dayalı kişiselleştirilmiş beslenme rehberliği sunuyorum.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: item.id * 0.1 }}
                className="text-center p-6 bg-nihal-secondary rounded-lg"
              >
                <div className="w-16 h-16 bg-nihal-primary rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white text-2xl font-bold">{item.id}</span>
                </div>
                <h3 className="text-xl font-bold text-nihal-dark mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="section-padding bg-nihal-secondary">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-nihal-dark mb-4">Ne Yapıyorum?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Sağlık ve yaşam hedeflerinize ulaşmanıza yardımcı olmak için çeşitli beslenme hizmetleri sunuyorum.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: service.id * 0.1 }}
                className="bg-white rounded-lg p-6 shadow-md text-center"
              >
                <div className="w-12 h-12 bg-nihal-primary rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold">{service.id}</span>
                </div>
                <h3 className="text-xl font-bold text-nihal-dark mb-3">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Newsletter */}
      
    </div>
  );
};

export default Home;